import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

export interface MultiSectionFilterSection {
  /** Query param / key the selection is reported under. */
  key: string;
  label: string;
  options: { label: string; value: any; isChecked: boolean }[];
  /** Show the search box above the checkboxes (long lists: categories, sites). */
  searchable?: boolean;
  /**
   * Count the section in "Filter (N)" even with every option ticked. Off by default: all ticked filters nothing. On
   * where "all" is itself a choice that differs from the default (the Issues tab's Status, whose default is a subset).
   */
  countWhenAll?: boolean;
  /** No "All" row: for sections of independent toggles (the sheet viewer's Show), where "all" means nothing */
  hideAll?: boolean;
}

/**
 * "Filter (N)" pill with a tabbed checkbox popover: the same look as the
 * daily reports' Status / Sites / Created by filter
 * (app-project-details-page-status-site-filter), driven by inputs so any tab
 * can list its own sections. Sections without options are not shown. The
 * parent owns the state (typically the url) and passes ticked options back in.
 */
@Component({
  selector: 'app-project-details-page-multi-section-filter',
  templateUrl: './multi-section-filter.component.html',
  styleUrls: ['../styles.scss', '../status-site-filter/status-site-filter.component.scss'],
  standalone: false,
})
export class MultiSectionFilterComponent implements OnChanges {
  @Input() sections: MultiSectionFilterSection[] = [];
  @Output() selectionChange = new EventEmitter<{ key: string; values: any[] }>();

  isOpen = false;
  activeKey: string = null;
  searchForm = new FormGroup<Record<string, FormControl<string>>>({});

  get visibleSections(): MultiSectionFilterSection[] {
    return (this.sections || []).filter(section => section.options?.length);
  }

  get activeSection(): MultiSectionFilterSection | undefined {
    return this.visibleSections.find(section => section.key === this.activeKey) || this.visibleSections[0];
  }

  /**
   * One per section that narrows the list: something ticked but not everything (every option ticked filters nothing),
   * unless the section counts "all" as a choice (`countWhenAll`).
   */
  get buttonLabel(): string {
    const active = this.visibleSections.filter(section => {
      const ticked = section.options.filter(option => option.isChecked).length;
      return ticked > 0 && (ticked < section.options.length || !!section.countWhenAll);
    }).length;
    return `Filter (${active})`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.sections) {
      (this.sections || []).forEach(section => {
        const control = `${section.key}Search`;
        if (!this.searchForm.contains(control)) {
          this.searchForm.addControl(control, new FormControl(''));
        }
      });
    }
  }

  selectTab(key: string): void {
    this.activeKey = key;
  }

  onSelectionChange(section: MultiSectionFilterSection, values: any[]): void {
    this.selectionChange.emit({ key: section.key, values });
  }
}
